import { Eye, ListFilter, Loader2, PencilLine } from "lucide-react";
import { STATUS_OPTIONS, StatusPill } from "./AdminShared";

export function AdminIncidentTable({
  incidents,
  incidentsLoading,
  statusFilter,
  onFilterChange,
  pendingCount,
  onViewFull,
  onEditStatus,
}) {
  return (
    <div data-testid="admin-incident-mgmt" className="mt-10 cre-surface rounded-sm p-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ListFilter className="h-4 w-4 text-blue-300" strokeWidth={1.5} />
          <div className="cre-overline text-blue-300">Incident management</div>
        </div>
        <div className="text-[11px] font-mono text-zinc-500">
          {incidents.length} shown · {pendingCount} awaiting approval
        </div>
      </div>

      <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="admin-status-filters">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            data-testid={`admin-filter-${opt.value}`}
            onClick={() => onFilterChange(opt.value)}
            className={`text-[11px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-sm border transition-colors ${
              statusFilter === opt.value
                ? "bg-sky-600 text-white border-sky-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-incidents-table">
          <thead>
            <tr className="text-left text-[11px] font-mono uppercase tracking-wider text-zinc-500 border-b border-slate-200">
              <th className="py-2 pr-3">Incident</th>
              <th className="py-2 pr-3">Severity</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Owner</th>
              <th className="py-2 pr-3">Created</th>
              <th className="py-2 pr-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {incidentsLoading ? (
              <tr>
                <td colSpan={6} className="py-6 text-center text-zinc-500">
                  <Loader2 className="h-4 w-4 mx-auto animate-spin" />
                </td>
              </tr>
            ) : incidents.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-6 text-center text-xs text-zinc-500">
                  No incidents match this filter.
                </td>
              </tr>
            ) : (
              incidents.map((row) => (
                <tr
                  key={row.id}
                  data-testid={`admin-incident-row-${row.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50/60"
                >
                  <td className="py-2 pr-3 min-w-[260px]">
                    <div className="text-zinc-100 truncate max-w-[360px]">
                      {row.title_preview}
                    </div>
                    <div className="text-[11px] font-mono text-zinc-500">
                      {row.domain || "domain=?"}{" "}
                      {row.feedback_count ? `· ${row.feedback_count} feedback` : ""}
                    </div>
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-zinc-300">
                    {row.severity}
                  </td>
                  <td className="py-2 pr-3">
                    <StatusPill status={row.status} />
                  </td>
                  <td className="py-2 pr-3 text-xs">
                    {row.user?.is_anonymous ? (
                      <span className="text-zinc-500 italic">anonymous</span>
                    ) : (
                      <span className="text-zinc-200">{row.user?.email}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-[11px] font-mono text-zinc-500 whitespace-nowrap">
                    {row.created_at?.slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      data-testid={`admin-view-full-${row.id}`}
                      onClick={() => onViewFull(row.id)}
                      title="Open full incident (logs an audit event)"
                      className="inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 rounded-sm transition-colors"
                    >
                      <Eye className="h-3 w-3" strokeWidth={1.75} />
                      View full
                    </button>
                    <button
                      type="button"
                      data-testid={`admin-change-status-${row.id}`}
                      onClick={() =>
                        onEditStatus({
                          id: row.id,
                          current: row.status,
                          next: "approved",
                          reason: "",
                          title: row.title_preview,
                        })
                      }
                      title="Override status"
                      className="ml-1.5 inline-flex items-center gap-1 text-[11px] font-mono px-2 py-1 border border-sky-600 text-sky-700 hover:bg-sky-50 rounded-sm transition-colors"
                    >
                      <PencilLine className="h-3 w-3" strokeWidth={1.75} />
                      Status
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
